import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Gift, Share2, Users, UserCheck, UserX, Zap } from "lucide-react";
import BottomNav from "../components/BottomNav";
import API from "../services/api";

function ReferEarn() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadSummary = async () => {
    if (!user?.id) return;
    try {
      const res = await API.get(`/referrals/${user.id}/summary`);
      setData(res.data || null);
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    if (!user?.id) { navigate("/login"); return; }
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, user?.id]);

  const copyCode = async () => {
    const code = data?.user?.referral_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert(code);
    }
  };

  const shareCode = async () => {
    const code = data?.user?.referral_code;
    if (!code) return;
    const url = `${window.location.origin}/login?tab=register&ref=${encodeURIComponent(code)}`;
    const text = `Join POS Matka with my referral code ${code} and start playing.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "POS Matka Referral", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        alert("Referral link copied");
      }
    } catch (_) {}
  };

  const claimWeekly = async () => {
    try {
      const res = await API.post(`/referrals/${user.id}/claim`);
      setData(res.data?.summary || data);
      alert("Referral bonus claimed!");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to claim referral bonus");
    }
  };

  const stats = data?.stats || {};
  const settings = data?.settings || {};
  const code = data?.user?.referral_code || "-----";
  const commPct = Number(settings.referral_commission_percent || 3);
  const weekLabel = stats.week_start && stats.week_end
    ? `${stats.week_start} → ${new Date(new Date(stats.week_end).getTime() - 86400000).toISOString().slice(0, 10)}`
    : "Monday to Sunday";

  return (
    <div style={page}>
      {/* Header */}
      <header style={header}>
        <button type="button" style={backBtn} onClick={() => navigate("/profile")}>
          <ArrowLeft size={20} color="#fff" />
        </button>
        <strong style={headerTitle}>Refer &amp; Earn</strong>
        <div style={{ width: 36 }} />
      </header>

      {/* Hero Banner */}
      <div style={heroBanner}>
        <div style={heroGlow} />
        <div style={heroContent}>
          <div style={giftIconWrap}><Gift size={32} color="#f97316" /></div>
          <div style={heroHeading}>Invite Friends &amp; Earn</div>
          <div style={heroSub}>You earn <strong style={{ color: "#f97316" }}>{commPct}%</strong> of your friend&apos;s weekly net loss</div>
          <div style={heroAmount}>₹{Number(stats.total_earned || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
          <div style={heroAmountLabel}>Total Earned</div>
        </div>
      </div>

      <main style={content}>
        {/* Referral Code Card */}
        <section style={codeCard}>
          <div style={codeLabelRow}>
            <span style={codeLabel}>Your Referral Code</span>
            <span style={codeHint}>Share to earn {commPct}%</span>
          </div>
          <div style={codeRow}>
            <span style={codeText}>{code}</span>
            <button type="button" style={copied ? copiedBtn : copyBtn} onClick={copyCode}>
              <Copy size={14} />
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <button type="button" style={shareBtn} onClick={shareCode}>
            <Share2 size={16} />
            Share Referral Link
          </button>
        </section>

        {/* Stats Grid */}
        <div style={statsGrid}>
          <StatTile icon={Users} label="Total Referred" value={stats.total_referrals || 0} color="#ea580c" />
          <StatTile icon={UserCheck} label="Active" value={stats.active_referrals || 0} color="#16a34a" />
          <StatTile icon={UserX} label="Inactive" value={stats.inactive_referrals || 0} color="#dc2626" />
        </div>

        {/* Earnings Card */}
        <section style={earningsCard}>
          <div style={earningsRow}>
            <div>
              <div style={earningsLabel}>Available to Claim</div>
              <div style={earningsValue}>₹{Number(stats.available_to_claim || 0).toFixed(2)}</div>
            </div>
            <Zap size={28} color="#f97316" />
          </div>
          <div style={earningsMini}>
            <MiniStat label="Total Earned" value={`₹${Number(stats.total_earned || 0).toFixed(2)}`} />
            <div style={miniDivider} />
            <MiniStat label="Pending" value={`₹${Number(stats.available_to_claim || 0).toFixed(2)}`} />
            <div style={miniDivider} />
            <MiniStat label="Redeemed" value={`₹${Number(stats.current_week_claimed || 0).toFixed(2)}`} />
          </div>
        </section>

        {/* Claim Card */}
        <section style={claimCard}>
          <div style={claimHead}>
            <div>
              <div style={claimTitle}>Weekly Claim</div>
              <div style={claimPeriod}>{weekLabel}</div>
            </div>
            <span style={pctBadge}>{commPct}%</span>
          </div>
          <div style={claimMeta}>
            <span>Deposits: <strong>₹{Number(stats.current_week_deposit_amount || 0).toFixed(2)}</strong></span>
            <span>Withdrawals: <strong>₹{Number(stats.current_week_withdraw_amount || 0).toFixed(2)}</strong></span>
            <span>Net Loss: <strong>₹{Number(stats.current_week_loss_amount || 0).toFixed(2)}</strong></span>
            <span>Min Claim: <strong>₹{Number(settings.referral_claim_min || 100).toFixed(2)}</strong></span>
          </div>
          <button
            type="button"
            style={{ ...claimBtn, opacity: stats.can_claim ? 1 : 0.45 }}
            disabled={!stats.can_claim}
            onClick={claimWeekly}
          >
            Claim Weekly Bonus
          </button>
          {!stats.can_claim && (
            <div style={claimNote}>
              {stats.is_claim_day
                ? `Minimum ₹${Number(settings.referral_claim_min || 100)} weekly loss required`
                : "Claims open every Monday"}
            </div>
          )}
        </section>

        {/* Referrals List */}
        <section style={listCard}>
          <div style={listHead}>
            <strong style={listTitle}>My Referrals</strong>
            <span style={listCount}>{(data?.referrals || []).length} users</span>
          </div>
          {(data?.referrals || []).length === 0 ? (
            <div style={emptyState}>
              <Users size={44} color="#fdba74" />
              <div style={emptyTitle}>No referrals yet</div>
              <div style={emptySubtitle}>Share your code to get started</div>
            </div>
          ) : (
            <div>
              {data.referrals.map((item) => (
                <div key={item.id} style={listRow}>
                  <div style={referralAvatar}>{String(item.name || "U").charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={rowName}>{item.name}</div>
                    <div style={rowPhone}>{item.phone}</div>
                  </div>
                  <span style={item.is_active ? activePill : inactivePill}>
                    {item.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <BottomNav />
    </div>
  );
}

function StatTile({ icon: Icon, label, value, color }) {
  return (
    <div style={statTile}>
      <div style={{ ...statIcon, background: `${color}18`, color }}><Icon size={20} /></div>
      <div style={statValue}>{value}</div>
      <div style={statLabel}>{label}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={miniStat}>
      <div style={miniLabel}>{label}</div>
      <div style={miniValue}>{value}</div>
    </div>
  );
}

/* ── Styles ── */
const page = { minHeight: "100vh", background: "#f0f0ff", paddingBottom: 84 };

const header = { height: 56, background: "linear-gradient(135deg,#431407,#7c2d12)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", position: "sticky", top: 0, zIndex: 20 };
const backBtn = { width: 34, height: 34, border: 0, borderRadius: 10, background: "rgba(255,255,255,0.12)", display: "grid", placeItems: "center", cursor: "pointer" };
const headerTitle = { fontSize: 16, fontWeight: 900, color: "#fff" };

const heroBanner = { position: "relative", background: "linear-gradient(160deg,#431407 0%,#7c2d12 60%,#7c2d12 100%)", padding: "28px 20px 36px", overflow: "hidden", textAlign: "center" };
const heroGlow = { position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(249,115,22,0.15)", pointerEvents: "none" };
const heroContent = { position: "relative", zIndex: 1 };
const giftIconWrap = { width: 60, height: 60, borderRadius: "50%", background: "rgba(249,115,22,0.15)", border: "2px solid rgba(249,115,22,0.3)", display: "grid", placeItems: "center", margin: "0 auto 14px" };
const heroHeading = { color: "#fff", fontWeight: 900, fontSize: 22, marginBottom: 6 };
const heroSub = { color: "rgba(255,255,255,0.7)", fontSize: 14, marginBottom: 18 };
const heroAmount = { color: "#f97316", fontWeight: 900, fontSize: 32, lineHeight: 1 };
const heroAmountLabel = { color: "rgba(255,255,255,0.5)", fontSize: 12, marginTop: 4 };

const content = { maxWidth: 520, margin: "0 auto", padding: "16px 14px", display: "grid", gap: 14 };

const codeCard = { background: "#fff", borderRadius: 20, padding: "18px 16px", boxShadow: "0 4px 16px rgba(79,70,229,0.08)", border: "1.5px solid #e8eaf6" };
const codeLabelRow = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 };
const codeLabel = { color: "#431407", fontWeight: 800, fontSize: 14 };
const codeHint = { color: "#ea580c", fontSize: 12, fontWeight: 700, background: "#f5f3ff", padding: "3px 8px", borderRadius: 8 };
const codeRow = { display: "flex", alignItems: "center", gap: 10, background: "#f5f3ff", borderRadius: 14, padding: "12px 14px", marginBottom: 12, border: "1.5px dashed #fdba74" };
const codeText = { flex: 1, fontSize: 22, fontWeight: 900, color: "#ea580c", letterSpacing: "0.12em" };
const copyBtn = { height: 36, padding: "0 14px", border: 0, borderRadius: 10, background: "#ea580c", color: "#fff", display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0 };
const copiedBtn = { ...copyBtn, background: "#16a34a" };
const shareBtn = { width: "100%", height: 46, border: 0, borderRadius: 14, background: "linear-gradient(135deg,#f97316,#ea580c)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: "0 4px 14px rgba(249,115,22,0.3)" };

const statsGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10 };
const statTile = { background: "#fff", borderRadius: 16, padding: "14px 10px", textAlign: "center", border: "1.5px solid #e8eaf6", boxShadow: "0 2px 8px rgba(79,70,229,0.05)" };
const statIcon = { width: 38, height: 38, borderRadius: 12, display: "grid", placeItems: "center", margin: "0 auto 8px" };
const statValue = { fontSize: 20, fontWeight: 900, color: "#431407" };
const statLabel = { fontSize: 11, color: "#94a3b8", marginTop: 3, fontWeight: 600 };

const earningsCard = { background: "linear-gradient(135deg,#431407,#ea580c)", borderRadius: 20, padding: "18px 16px" };
const earningsRow = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 };
const earningsLabel = { color: "rgba(255,255,255,0.65)", fontSize: 13, marginBottom: 4 };
const earningsValue = { color: "#fff", fontWeight: 900, fontSize: 26 };
const earningsMini = { display: "grid", gridTemplateColumns: "1fr auto 1fr auto 1fr", gap: 0, background: "rgba(255,255,255,0.08)", borderRadius: 14, padding: "14px 10px" };
const miniStat = { textAlign: "center" };
const miniLabel = { color: "rgba(255,255,255,0.6)", fontSize: 11 };
const miniValue = { color: "#fff", fontWeight: 800, fontSize: 13, marginTop: 4 };
const miniDivider = { width: 1, background: "rgba(255,255,255,0.15)", margin: "0 4px" };

const claimCard = { background: "#fff", borderRadius: 20, padding: "18px 16px", border: "1.5px solid #e8eaf6", boxShadow: "0 2px 10px rgba(79,70,229,0.06)" };
const claimHead = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 };
const claimTitle = { fontWeight: 800, color: "#431407", fontSize: 15 };
const claimPeriod = { color: "#64748b", fontSize: 12, marginTop: 3 };
const pctBadge = { background: "linear-gradient(135deg,#f97316,#ea580c)", color: "#fff", borderRadius: 10, padding: "6px 12px", fontWeight: 900, fontSize: 15 };
const claimMeta = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", color: "#64748b", fontSize: 13, marginBottom: 14 };
const claimBtn = { width: "100%", height: 48, border: 0, borderRadius: 14, background: "linear-gradient(135deg,#431407,#ea580c)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: "0 4px 14px rgba(79,70,229,0.3)" };
const claimNote = { marginTop: 10, textAlign: "center", color: "#94a3b8", fontSize: 12, background: "#f8fafc", borderRadius: 10, padding: "10px 12px" };

const listCard = { background: "#fff", borderRadius: 20, overflow: "hidden", border: "1.5px solid #e8eaf6" };
const listHead = { padding: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f0f0ff" };
const listTitle = { color: "#431407", fontWeight: 800, fontSize: 15 };
const listCount = { background: "#fff7ed", color: "#ea580c", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700 };
const emptyState = { padding: "32px 16px", textAlign: "center", display: "grid", justifyItems: "center", gap: 8 };
const emptyTitle = { fontSize: 16, color: "#ea580c", fontWeight: 700 };
const emptySubtitle = { color: "#94a3b8", fontSize: 13 };
const listRow = { padding: "12px 16px", borderBottom: "1px solid #f8f8ff", display: "flex", alignItems: "center", gap: 12 };
const referralAvatar = { width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#ea580c,#ea580c)", color: "#fff", fontWeight: 900, fontSize: 14, display: "grid", placeItems: "center", flexShrink: 0 };
const rowName = { fontWeight: 700, color: "#431407", fontSize: 14 };
const rowPhone = { color: "#94a3b8", fontSize: 12, marginTop: 2 };
const activePill = { background: "#dcfce7", color: "#15803d", padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, flexShrink: 0 };
const inactivePill = { background: "#f1f5f9", color: "#64748b", padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, flexShrink: 0 };

export default ReferEarn;
